import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router'
import {
  Box,
  Stack,
  Heading,
  Text,
  Button,
  Alert,
  FormControl,
  Input,
  Checkbox,
  Spinner,
  Card,
  Link,
  ImageLogo,
  Badge,
  Icon
} from '@/components/lib'
import { FaChevronDown } from 'react-icons/fa'
import api from '../api'

import RichTextViewer from './includes/RichTextViewer'
import PhoneInput from './includes/PhoneInput'
import Terms from './includes/Terms'
import VerificationBox from './includes/VerificationBox'
import useContactVerification from '../hooks/useContactVerification'
import BusStopImage from '../images/busstop.png'

const POLL_INTERVAL_MS = 10000

const METHOD_LABELS = {
  email: 'email address',
  phone: 'phone number',
  whatsapp: 'WhatsApp number'
}

function describeMethods (methods) {
  const labels = methods.map((m) => METHOD_LABELS[m] || m)
  if (labels.length < 2) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

const _sessionKey = (docId) => `consentSession:${docId}`
function getStoredSession (docId) {
  try { return JSON.parse(window.sessionStorage.getItem(_sessionKey(docId))) } catch { return null }
}
function storeSession (docId, sessionId, token) {
  try { window.sessionStorage.setItem(_sessionKey(docId), JSON.stringify({ sessionId, token })) } catch {}
}
function clearStoredSession (docId) {
  try { window.sessionStorage.removeItem(_sessionKey(docId)) } catch {}
}

export default function Consent () {
  const [trial, setTrial] = useState()
  const [consentDocument, setConsentDocument] = useState()
  const [consentSession, setConsentSession] = useState()
  const [sessionToken, setSessionToken] = useState()
  const [error, setError] = useState()
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [fullName, setFullName] = useState('')
  const [eligibilityCriteriaConfirmed, setEligibilityCriteriaConfirmed] =
    useState(false)
  const [eligibilityCriteriaInitials, setEligibilityCriteriaInitials] =
    useState('')
  const [conditionConfirmations, setConditionConfirmations] = useState({})
  const [conditionInitials, setConditionInitials] = useState({})
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [conditionsConsentGiven, setConditionsConsentGiven] = useState(false)
  const [consentGiven, setConsentGiven] = useState(false)
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [identityVerificationUrl, setIdentityVerificationUrl] = useState('')
  const [verification, setVerification] = useState(null)
  const [verificationStatus, setVerificationStatus] = useState(null)
  const [verificationError, setVerificationError] = useState('')
  const [identityVerificationNames, setIdentityVerificationNames] = useState([])
  const [identityNameWarning, setIdentityNameWarning] = useState(false)
  const [agreeToKycaidTerms, setAgreeToKycaidTerms] = useState(false)
  const [fieldSaveState, setFieldSaveState] = useState({})
  const [failedSaves, setFailedSaves] = useState({})
  const [sessionLoadError, setSessionLoadError] = useState(null)
  const pollRef = useRef(null)

  const { trialIdOrCode, documentId } = useParams()

  useEffect(() => {
    if (!trialIdOrCode) setError('No trial could be found.')
    else {
      const fb = (err) => {
        setError(
          'There was an error fetching the consent document for this trial.' +
            err.message
        )
      }
      const qs = new URLSearchParams(window.location.search)
      const qDocumentId = (qs.get('documentId') || '').trim()
      const qFormKey = (qs.get('formKey') || '').trim()

      // Backwards compatibility:
      // - Old URL used `/:documentId` where it might actually be a formKey.
      // - New URL uses query params.
      let effectiveDocumentId = qDocumentId || null
      let effectiveFormKey = qFormKey || null
      if (!effectiveDocumentId && !effectiveFormKey && documentId) {
        const looksLikeObjectId = /^[a-f0-9]{24}$/i.test(documentId)
        if (looksLikeObjectId) effectiveDocumentId = documentId
        else effectiveFormKey = documentId
      }

      api.trials.getConsentDocumentPublic(
        trialIdOrCode,
        { documentId: effectiveDocumentId, formKey: effectiveFormKey },
        (data) => {
          setTrial(data?.trial)
          const doc = data?.document
          setConsentDocument(doc)
          if (!doc?._id) return

          function hydrateSession (session, token) {
            setConsentSession(session)
            setSessionToken(token)
            const d = session.data || {}
            setFullName(d.fullName || '')
            setEmail(d.email || '')
            setPhone(d.phone || '')
            setWhatsapp(d.whatsapp || '')
            setEligibilityCriteriaConfirmed(d.eligibilityCriteriaConfirmed || false)
            setEligibilityCriteriaInitials(d.eligibilityCriteriaInitials || '')
            setTermsAgreed(d.processingConsentGiven || false)
            setConditionsConsentGiven(d.trialConditionsConsentGiven || false)
            setConsentGiven(d.consentGiven || false)
            const confirmations = {}
            const initials = {}
            for (const [id, val] of Object.entries(d.conditions || {})) {
              if (val.checked != null) confirmations[id] = val.checked
              if (val.initials) initials[id] = val.initials
            }
            setConditionConfirmations(confirmations)
            setConditionInitials(initials)
            setAgreeToKycaidTerms(d.agreeToKycaidTerms || false)
            if (session.identityVerification) {
              setVerification(session.identityVerification)
            }
            if (session.status === 'completed') {
              setSuccess('Your consent has been successfully recorded.')
            }
          }

          function createNewSession () {
            api.consentSessions.create(
              doc._id,
              {},
              (sessionData) => {
                storeSession(doc._id, sessionData._id, sessionData.token)
                setConsentSession(sessionData)
                setSessionToken(sessionData.token)
              },
              (err) => {
                setSessionLoadError(err.message || 'Failed to start consent session.')
              }
            )
          }

          const stored = getStoredSession(doc._id)
          if (stored) {
            api.consentSessions.get(
              doc._id,
              stored.sessionId,
              stored.token,
              (session) => hydrateSession(session, stored.token),
              () => {
                clearStoredSession(doc._id)
                createNewSession()
              }
            )
          } else {
            createNewSession()
          }
        },
        fb
      )
    }
  }, [trialIdOrCode, documentId])

  useEffect(() => {
    if (
      !verification ||
      !consentSession?._id ||
      !sessionToken ||
      !consentDocument?._id ||
      verificationStatus === 'verified' ||
      verificationStatus === 'failed'
    ) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    function poll () {
      api.consentSessions.getIdentityVerification(
        consentDocument._id,
        consentSession._id,
        sessionToken,
        verification,
        (data) => {
          setVerificationStatus(data?.status ?? null)
          setIdentityVerificationNames(
            data?.names?.length ? data.names : data?.name ? [data.name] : []
          )
          if (data?.status === 'failed') {
            setVerificationError(
              'Your identity verification was unsuccessful.'
            )
          }
        },
        () => {}
      )
    }
    poll()
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [
    consentDocument?._id,
    consentSession?._id,
    sessionToken,
    verification,
    verificationStatus
  ])

  function logConsentEvent (eventName, payload = {}) {
    if (!consentSession?._id || !sessionToken || !consentDocument?._id) return
    api.consentSessions.postEvent(
      consentDocument._id,
      consentSession._id,
      sessionToken,
      { type: eventName, data: payload },
      () => {},
      (err) => {
        console.error('Consent event failed:', eventName, err)
      }
    )
  }

  function normalizeIdentityName (value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^\p{Letter}\p{Number}]/gu, '')
  }

  function identityNamesMatch (value, names) {
    const normalized = normalizeIdentityName(value)
    if (!normalized) return false
    return (names || []).some((name) => normalizeIdentityName(name) === normalized)
  }

  function getUpdateEntries (updates) {
    // Normalize one update payload into field-level retry units so failed saves
    // can be tracked/retried per field (including nested `conditions.*`).
    const entries = []
    for (const [field, value] of Object.entries(updates || {})) {
      if (field === 'conditions') {
        for (const [conditionId, conditionUpdates] of Object.entries(
          value || {}
        )) {
          for (const [conditionField, conditionValue] of Object.entries(
            conditionUpdates || {}
          )) {
            if (conditionValue === null || conditionValue === undefined) { continue }
            entries.push({
              key: `conditions.${conditionId}.${conditionField}`,
              updates: {
                conditions: {
                  [conditionId]: { [conditionField]: conditionValue }
                }
              }
            })
          }
        }
        continue
      }
      if (value === null || value === undefined) continue
      entries.push({ key: field, updates: { [field]: value } })
    }
    return entries
  }

  function updateSession (updates) {
    if (!consentSession?._id || !sessionToken || !consentDocument?._id) return
    const entries = getUpdateEntries(updates)
    const keys = entries.map((e) => e.key)
    if (keys.length > 0) {
      setFieldSaveState((prev) => {
        const next = { ...prev }
        for (const key of keys) next[key] = { status: 'saving', error: '' }
        return next
      })
      setFailedSaves((prev) => {
        const next = { ...prev }
        for (const key of keys) delete next[key]
        return next
      })
    }
    return new Promise((resolve) => {
      api.consentSessions.update(
        consentDocument._id,
        consentSession._id,
        sessionToken,
        updates,
        (updatedSession) => {
          setConsentSession(updatedSession)
          setError(null)
          if (keys.length > 0) {
            setFieldSaveState((prev) => {
              const next = { ...prev }
              for (const key of keys) { next[key] = { status: 'saved', error: '' } }
              return next
            })
          }
          resolve({ ok: true, session: updatedSession })
        },
        (err) => {
          if (keys.length > 0) {
            setFieldSaveState((prev) => {
              const next = { ...prev }
              for (const key of keys) { next[key] = { status: 'failed', error: err.message } }
              return next
            })
            setFailedSaves((prev) => {
              const next = { ...prev }
              for (const entry of entries) {
                next[entry.key] = {
                  updates: entry.updates,
                  error: err.message
                }
              }
              return next
            })
          }
          setError(err.message)
          resolve({ ok: false, error: err })
        }
      )
    })
  }

  async function retryFailedSaves () {
    const retryEntries = Object.values(failedSaves)
    if (retryEntries.length === 0) return
    for (const entry of retryEntries) {
      await updateSession(entry.updates)
    }
  }

  const {
    stateFor: verificationStateFor,
    request: requestVerificationCode,
    verify: verifyCode,
    setCode: setVerificationCode
  } = useContactVerification({
    onEvent: logConsentEvent,
    requestCode: (method, done, fail) => {
      // Must report back rather than return silently: the hook has already
      // moved this method into 'sending' and only done/fail can move it out.
      if (!consentSession?._id || !sessionToken || !consentDocument?._id) {
        return fail({ message: 'Your consent form is still loading. Please try again in a moment.' })
      }
      api.consentSessions.requestVerification(
        consentDocument._id, consentSession._id, sessionToken, method, done, fail
      )
    },
    submitCode: (method, code, done, fail) => {
      if (!consentSession?._id || !sessionToken || !consentDocument?._id) {
        return fail({ message: 'Your consent form is still loading. Please try again in a moment.' })
      }
      api.consentSessions.verifyVerification(
        consentDocument._id, consentSession._id, sessionToken, method, code, done, fail
      )
    },
    // Refresh the session so verifiedMethods reflects the new verification.
    onVerified: () => {
      api.consentSessions.get(
        consentDocument._id,
        consentSession._id,
        sessionToken,
        setConsentSession,
        () => {}
      )
    }
  })

  function resetConsent () {
    if (consentDocument?._id) clearStoredSession(consentDocument._id)
    window.location.reload()
  }

  function isVerified (method) {
    return consentSession?.verifiedMethods?.includes(method) || false
  }

  // The methods the participant has no choice but to provide. Any method the
  // trial marked as required, plus — when only one method is offered at all —
  // that method, since consent always needs at least one verified contact.
  function getRequiredMethods () {
    const enabled = consentDocument?.verificationMethods || []
    const required = (
      consentDocument?.requiredVerificationMethods || []
    ).filter((m) => enabled.includes(m))
    if (!required.length && enabled.length === 1) return enabled
    return required
  }

  function createIdentityVerification () {
    if (!consentSession?._id || !sessionToken || !consentDocument?._id) return
    setLoading(true)
    setError('')
    setVerificationError('')
    setIdentityVerificationNames([])
    setIdentityNameWarning(false)
    setVerificationStatus(null)
    api.consentSessions.createIdentityVerification(
      consentDocument._id,
      consentSession._id,
      sessionToken,
      (data) => {
        setLoading(false)
        setIdentityVerificationUrl(data?.kycaidUrl || '')
        setVerification(data?.verification ?? null)
        setVerificationStatus('pending_verification')
      },
      (err) => {
        setLoading(false)
        setError(err.message)
      }
    )
  }

  function checkIdentityName (value) {
    if (
      !consentDocument?.identityVerificationEnabled ||
      !identityVerificationNames.length ||
      !value
    ) {
      setIdentityNameWarning(false)
      return
    }
    setIdentityNameWarning(!identityNamesMatch(value, identityVerificationNames))
  }

  async function consent (e) {
    const hasPendingSavesNow = Object.values(fieldSaveState).some(
      (s) => s.status === 'saving'
    )
    const hasFailedSavesNow = Object.values(fieldSaveState).some(
      (s) => s.status === 'failed'
    )
    if (hasPendingSavesNow) { return setError('Please wait while your latest changes are being saved.') }
    if (hasFailedSavesNow) {
      return setError(
        'Some answers failed to save. Please retry failed saves before submitting.'
      )
    }
    if (!termsAgreed) {
      return setError(
        'You must agree to the Trialflare terms before continuing.'
      )
    }
    if (!conditionsConsentGiven) { return setError('You must agree to the trial terms before continuing.') }
    if (!consentGiven) {
      return setError(
        'You must confirm your consent to take part in this trial.'
      )
    }
    if (
      consentDocument?.identityVerificationEnabled &&
      verificationStatus !== 'verified'
    ) {
      return setError('Please complete identity verification above first.')
    }
    const missingRequired = getRequiredMethods().filter((m) => !isVerified(m))
    if (missingRequired.length) {
      return setError(
        `You must provide and verify your ${describeMethods(missingRequired)} before continuing.`
      )
    }
    setLoading(true)
    setError('')

    // Flush text fields to the session before completing — guards against the
    // case where the user clicked Submit without first blurring an input field,
    // which would leave the session without the latest value and trigger a
    // tamper-check mismatch on the backend.
    const flush = await updateSession({
      fullName,
      email: email || null,
      phone: phone || null,
      whatsapp: whatsapp || null
    })
    if (!flush?.ok) {
      setLoading(false)
      return
    }

    const data = {
      phone: phone || null,
      whatsapp: whatsapp || null,
      email: email || null,
      fullName,
      consentGiven,
      trialConditionsConsentGiven: conditionsConsentGiven,
      processingConsentGiven: termsAgreed,
      eligibilityCriteriaConfirmed,
      eligibilityCriteriaInitials,
      conditionConfirmations,
      conditionInitials
    }
    if (consentDocument?.identityVerificationEnabled) {
      data.agreeToKycaidTerms = agreeToKycaidTerms
    }
    api.consentSessions.complete(
      consentDocument._id,
      consentSession._id,
      sessionToken,
      data,
      (d) => {
        setLoading(false)
        setSuccess('Your consent has been successfully recorded.')
      },
      (err) => {
        setLoading(false)
        setError(err.message)
      }
    )
  }

  if (!trial || !consentDocument || !consentSession) {
    return (
      <Stack gap={4} my={10} maxW='500px' mx='auto' bg='bg.surface' borderColor='border.default' borderWidth='1px' p={6} rounded='md' alignItems='center'>
        {sessionLoadError
          ? (
            <>
              <Alert status='error'>
                There was a problem starting your consent session. Please try refreshing the page.
              </Alert>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </>
            )
          : error
            ? <Alert status='error'>{error}</Alert>
            : <Spinner size='lg' />}
      </Stack>
    )
  }

  const verificationComplete = verificationStatus === 'verified'
  const verificationSubmitted = verificationStatus === 'submitted'
  const verificationFailed = verificationStatus === 'failed'
  const verificationPending = !!verification && !verificationComplete && !verificationFailed
  const verificationInProgress = verificationPending && !!identityVerificationUrl
  const hasVerifiedContact = consentSession?.verifiedMethods?.length > 0
  const requiredMethods = getRequiredMethods()
  const allRequiredVerified = requiredMethods.every((m) => isVerified(m))
  const enabledMethods = consentDocument.verificationMethods || []
  // Only genuinely-required methods are marked. Nothing is labelled "optional":
  // consent always needs at least one contact method verified, so an unmarked
  // field is one the participant may skip *if they verify another*, which the
  // text above the fields explains. Marking them all optional contradicted it.
  const methodLabel = (text, method) => (
    <>
      {text}
      {requiredMethods.includes(method) && (
        <Badge colorPalette='red' ml={2}>Required</Badge>
      )}
    </>
  )
  const hasPendingSaves = Object.values(fieldSaveState).some(
    (s) => s.status === 'saving'
  )
  const hasFailedSaves = Object.values(fieldSaveState).some(
    (s) => s.status === 'failed'
  )
  const canSubmitConsent =
    (!consentDocument?.identityVerificationEnabled || verificationComplete) &&
    hasVerifiedContact &&
    allRequiredVerified &&
    !hasPendingSaves &&
    !hasFailedSaves

  // Control flow booleans
  const shouldShowVerification =
    consentSession.data?.pisReadConfirmed &&
    consentDocument.identityVerificationEnabled
  const shouldShowConditions =
    consentSession.data?.pisReadConfirmed &&
    (!shouldShowVerification || verificationComplete)
  return (
    <Stack
      gap={10}
      my={10}
      maxW='500px'
      mx='auto'
      bg='bg.surface'
      borderColor='border.default'
      borderWidth='1px'
      p={3}
      rounded='md'
    >
      {trial?.sponsorLogoUrl && (
        <ImageLogo
          display='flex'
          justifyContent='center'
          src={trial.sponsorLogoUrl}
          alt={`${trial.sponsor} logo`}
          h='100px'
        />
      )}

      <Stack p={3} rounded='md' textAlign='center'>
        <Heading size='lg'>{trial?.name}</Heading>
        <Heading size='sm' color='fg.muted'>
          Participation eConsent
        </Heading>
      </Stack>

      {trial?.eConsentClosed && (
        <Stack>
          <Heading size='md'>eConsent is closed</Heading>
          <Box display='flex' justifyContent='center'>
            <img src={BusStopImage} alt='Bus stop' maxW='400px' />
          </Box>
          <Text>
            Thank you for your interest in taking part in this study. However,
            the study is now closed to new participants and they can no longer
            take part.
          </Text>
          <Text>
            If you have any questions or concerns, please contact the study team
            directly.
          </Text>
        </Stack>
      )}

      {success && (
        <Stack p={3} bg='bg.subtle' rounded='md'>
          <Alert variant='left-accent' status='success'>
            Thank you. {success}
          </Alert>
          {consentDocument?.postSubmissionText?.length > 0
            ? (
              <RichTextViewer content={consentDocument.postSubmissionText} />
              )
            : (
              <Text>
                Thank you for your consent. You can now close this browser tab.
              </Text>
              )}
          <Button variant='ghost' size='sm' color='fg.subtle' onClick={resetConsent}>
            Start a new consent session
          </Button>
        </Stack>
      )}

      {!trial?.eConsentClosed && !success && (
        <>
          <Alert status='info'>
            <Text fontWeight='bold'>
              Please read this page carefully and follow the instructions to
              confirm your consent.
            </Text>
          </Alert>

          {consentDocument?.identityVerificationEnabled && (
            <Alert status='info'>
              As part of this process you will need to verify your identity
              using your ID and a selfie. Please ensure you have your phone and
              ID to-hand.
            </Alert>
          )}

          <Stack gap={3} bg='bg.subtle' p={3} rounded='md'>
            <Heading size='lg'>Participant Information Sheet (PIS)</Heading>
            <RichTextViewer content={consentDocument.document} />

            {consentDocument.eligibilityCriteria && (
              <Card>
                <Heading size='lg'>Eligibility criteria</Heading>
                <RichTextViewer content={consentDocument.eligibilityCriteria} />

                <Stack bg='bg.accent' p={3}>
                  <Checkbox
                    isChecked={eligibilityCriteriaConfirmed}
                    onChange={(e) => {
                      setEligibilityCriteriaConfirmed(e.checked)
                      logConsentEvent('eligibilityCriteriaCheckboxChanged', {
                        checked: e.checked
                      })
                      updateSession({
                        eligibilityCriteriaConfirmed: e.checked
                      })
                    }}
                  >
                    I confirm that I am able to take part.
                  </Checkbox>
                  {eligibilityCriteriaConfirmed && (
                    <Box>
                      <Text fontSize='sm'>
                        Please add your initials below to confirm.
                      </Text>
                      <Input
                        autoFocus
                        bg='bg.surface'
                        size='sm'
                        placeholder='A.B.'
                        value={eligibilityCriteriaInitials}
                        onChange={(e) =>
                          setEligibilityCriteriaInitials(e.target.value)}
                        onBlur={(e) => {
                          const value = e.target.value
                          logConsentEvent(
                            'eligibilityCriteriaInitialsProvided',
                            { value }
                          )
                          updateSession({ eligibilityCriteriaInitials: value })
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </Card>
            )}

            {!consentSession.data?.pisReadConfirmed && (!consentDocument.eligibilityCriteria || (eligibilityCriteriaConfirmed && eligibilityCriteriaInitials)) && (
              <>
                {error && (
                  <Box>
                    <Alert variant='left-accent' status='error'>
                      {error}
                    </Alert>
                  </Box>
                )}
                <Button
                  colorPalette='primary'
                  onClick={() => {
                    logConsentEvent('pisReadConfirmed')
                    updateSession({ pisReadConfirmed: true })
                  }}
                  isDisabled={loading}
                >
                  I've read the participant information sheet
                </Button>
              </>
            )}
            {consentSession.data?.pisReadConfirmed && (
              <ScrollContinue />
            )}
          </Stack>

          {shouldShowVerification && (
            <Stack bg='bg.subtle' p={3} gap={5}>
              <Box>
                <Heading size='md'>Identity verification</Heading>
                <Text>
                  To continue, please verify your identity using a
                  government-issued ID and a selfie.
                </Text>
              </Box>
              {!verification && (
                <>
                  <Alert status='info'>
                    In order to verify your identity, our partner (KYCAID) will
                    securely process your data.
                  </Alert>
                  <Checkbox
                    isChecked={agreeToKycaidTerms}
                    onChange={(e) => {
                      setAgreeToKycaidTerms(e.checked)
                      if (e.checked) logConsentEvent('kycaidTermsAgreed')
                      updateSession({ agreeToKycaidTerms: e.checked })
                    }}
                  >
                    I consent to KYCAID's{' '}
                    <Link
                      href='https://kycaid.com/privacy-policy/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Privacy Policy
                    </Link>{' '}
                    and to their processing of my personal data for the purpose
                    of identity verification.
                  </Checkbox>
                  {agreeToKycaidTerms && (
                    <Button
                      colorPalette='primary'
                      onClick={createIdentityVerification}
                      isLoading={loading}
                    >
                      Verify your identity
                    </Button>
                  )}
                </>
              )}
              {verificationPending && !identityVerificationUrl && verificationStatus === null && (
                <Stack direction='row' gap={3} alignItems='center'>
                  <Spinner size='sm' />
                  <Text fontSize='sm' color='fg.muted'>Checking your verification status...</Text>
                </Stack>
              )}
              {verificationPending && !identityVerificationUrl && verificationStatus === 'pending_verification' && (
                <Stack gap={3}>
                  <Alert status='info'>
                    Your identity verification was started but not yet completed. You can resume it below.
                  </Alert>
                  <Button
                    colorPalette='primary'
                    onClick={createIdentityVerification}
                    isLoading={loading}
                  >
                    Resume identity verification
                  </Button>
                </Stack>
              )}
              {verificationInProgress && (
                <Stack gap={5}>
                  <Box minH='835px' minW='320px' w='100%'>
                    <iframe
                      id='kycaid-verification-iframe'
                      src={identityVerificationUrl}
                      title='Identity verification'
                      allow='microphone *;camera *;midi *;encrypted-media *;clipboard-read;clipboard-write;'
                      style={{
                        width: '100%',
                        minHeight: '835px',
                        border: 'none'
                      }}
                    />
                  </Box>
                  <Stack
                    gap={5}
                    direction='row'
                    alignItems='center'
                    justifyContent='start'
                  >
                    <Box>
                      <Spinner size='lg' />
                    </Box>
                    <Text>
                      Please complete the verification process in the box above
                      and don't leave or refresh this page. This page will
                      automatically update as the process progresses.
                    </Text>
                  </Stack>
                </Stack>
              )}
              {verificationSubmitted && (
                <Alert variant='left-accent' status='info'>
                  <Stack direction='row' gap={5} alignItems='center'>
                    <Box>
                      <Spinner size='lg' />
                    </Box>
                    <Text>
                      Your identity verification has been submitted and is still
                      being reviewed. Please wait here until it has been
                      successfully verified before continuing.
                    </Text>
                  </Stack>
                </Alert>
              )}
              {verificationFailed && (
                <Stack gap={3}>
                  <Alert variant='left-accent' status='error'>
                    {verificationError}
                  </Alert>
                  <Text>
                    Sorry, your identity verification was unsuccessful and you
                    cannot continue with this consent. You can try to reset this consent
                    to try again or contact us on{' '}
                    <Link href='mailto:support@trialflare.com'>
                      support@trialflare.com
                    </Link> for assistance.
                  </Text>
                </Stack>
              )}
              {verificationComplete && (
                <>
                  <Alert variant='left-accent' status='success'>
                    Identity verified. You can now continue.
                  </Alert>
                  <ScrollContinue />
                </>
              )}
            </Stack>
          )}

          {shouldShowConditions && (
            <>
              {consentDocument.conditions?.length > 0 && (
                <Stack gap={5} bg='bg.subtle' p={3} rounded='md'>
                  <Heading size='lg'>Trial conditions</Heading>
                  {consentDocument.conditions.map((condition, i) => (
                    <Stack key={i} bg='bg.muted' p={3} rounded='md'>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Heading size='sm'>{condition.title}</Heading>
                        {condition.optional === true && (
                          <Badge size='sm' colorPalette='blue'>
                            Optional
                          </Badge>
                        )}
                      </Stack>
                      <Text fontSize='sm'>{condition.description}</Text>
                      <Stack bg='bg.accent' p={3}>
                        <Checkbox
                          isChecked={conditionConfirmations[condition.id]}
                          onChange={(e) => {
                            const newConditionConfirmations = Object.assign(
                              {},
                              conditionConfirmations
                            )
                            newConditionConfirmations[condition.id] = e.checked
                            setConditionConfirmations(
                              newConditionConfirmations
                            )
                            logConsentEvent('conditionCheckboxChanged', {
                              conditionId: condition.id,
                              title: condition.title,
                              checked: e.checked
                            })
                            updateSession({
                              conditions: {
                                [condition.id]: { checked: e.checked }
                              }
                            })
                          }}
                        >
                          I confirm that I agree to this.
                        </Checkbox>
                        {conditionConfirmations[condition.id] === true && (
                          <Stack>
                            <Text fontSize='sm'>
                              Please add your initials below to confirm.
                            </Text>
                            <Input
                              autoFocus
                              bg='bg.surface'
                              size='sm'
                              placeholder='A.B.'
                              value={conditionInitials[condition.id] || ''}
                              onChange={(e) => {
                                const newConditionInitials = Object.assign(
                                  {},
                                  conditionInitials
                                )
                                newConditionInitials[condition.id] =
                                  e.target.value
                                setConditionInitials(newConditionInitials)
                              }}
                              onBlur={(e) => {
                                const value = e.target.value
                                logConsentEvent('conditionInitialsProvided', {
                                  conditionId: condition.id,
                                  value
                                })
                                updateSession({
                                  conditions: {
                                    [condition.id]: { initials: value }
                                  }
                                })
                              }}
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Stack bg='bg.subtle' p={3} gap={5}>
                <Box>
                  <Heading size='lg'>About you</Heading>
                  <Text>
                    To continue, we need to collect some information about you.
                  </Text>
                  {requiredMethods.length > 0
                    ? (
                      <Text>
                        You must provide and verify your{' '}
                        {describeMethods(requiredMethods)}
                        {enabledMethods.length > requiredMethods.length
                          ? '. The other contact details below are optional.'
                          : '.'}
                      </Text>
                      )
                    : (
                        enabledMethods.length > 1 && (
                          <Text>
                            You must provide and verify at least one of the
                            contact details below.
                          </Text>
                        )
                      )}
                </Box>

                <FormControl label='Full name (as it appears on your ID)'>
                  <Input
                    bg='bg.surface'
                    type='text'
                    id='fullName'
                    placeholder='Jane Smith'
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onBlur={(e) => {
                      logConsentEvent('fullNameProvided', {
                        value: e.target.value
                      })
                      checkIdentityName(e.target.value)
                      updateSession({ fullName: e.target.value })
                    }}
                  />
                  {identityNameWarning && (
                    <Alert status='warning' variant='left-accent' mt={2}>
                      The name you entered does not match the name on your ID
                      verification.
                      {identityVerificationNames.length > 0 && (
                        <>
                          {' '}Please enter your name exactly as it appears on
                          your ID:{' '}
                          <Text as='span' fontWeight='bold'>
                            {identityVerificationNames.join(' or ')}
                          </Text>
                          .
                        </>
                      )}
                    </Alert>
                  )}
                </FormControl>

                {consentDocument.verificationMethods?.includes('phone') && (
                  <FormControl
                    label={methodLabel('Mobile phone number for text messages', 'phone')}
                    helperText='You must be able to receive SMS/text messages on this number.'
                  >
                    {isVerified('phone')
                      ? (
                        <Alert status='success' size='sm' variant='left-accent'>
                          Phone number verified: {phone}
                        </Alert>
                        )
                      : (
                        <>
                          <PhoneInput
                            bg='bg.surface'
                            value={phone}
                            onChange={(v) => setPhone(v)}
                            onBlur={() => {
                              logConsentEvent('phoneProvided', { value: phone })
                              updateSession({ phone })
                            }}
                            placeholder='Enter your phone (SMS) number'
                          />
                          <VerificationBox
                            vs={verificationStateFor('phone')}
                            contactValue={phone}
                            onRequestCode={() => requestVerificationCode('phone')}
                            onCodeChange={(val) => setVerificationCode('phone', val)}
                            onVerify={() => verifyCode('phone')}
                          />
                        </>
                        )}
                  </FormControl>
                )}

                {consentDocument.verificationMethods?.includes('whatsapp') && (
                  <FormControl label={methodLabel('WhatsApp number', 'whatsapp')}>
                    {isVerified('whatsapp')
                      ? (
                        <Alert status='success' size='sm' variant='left-accent'>
                          WhatsApp number verified: {whatsapp}
                        </Alert>
                        )
                      : (
                        <>
                          <PhoneInput
                            bg='bg.surface'
                            value={whatsapp}
                            onChange={(v) => setWhatsapp(v)}
                            onBlur={() => {
                              logConsentEvent('whatsappProvided', {
                                value: whatsapp
                              })
                              updateSession({ whatsapp })
                            }}
                            placeholder='Enter your WhatsApp number'
                          />
                          <VerificationBox
                            vs={verificationStateFor('whatsapp')}
                            contactValue={whatsapp}
                            onRequestCode={() => requestVerificationCode('whatsapp')}
                            onCodeChange={(val) => setVerificationCode('whatsapp', val)}
                            onVerify={() => verifyCode('whatsapp')}
                          />
                        </>
                        )}
                  </FormControl>
                )}

                {consentDocument.verificationMethods?.includes('email') && (
                  <FormControl label={methodLabel('Email address', 'email')}>
                    {isVerified('email')
                      ? (
                        <Alert status='success' size='sm' variant='left-accent'>
                          Email verified with address {email}
                        </Alert>
                        )
                      : (
                        <>
                          <Input
                            bg='bg.surface'
                            type='email'
                            id='email'
                            placeholder='jane@company.com'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={(e) => {
                              logConsentEvent('emailProvided', {
                                value: e.target.value
                              })
                              updateSession({ email: e.target.value })
                            }}
                          />
                          <VerificationBox
                            vs={verificationStateFor('email')}
                            contactValue={email}
                            onRequestCode={() => requestVerificationCode('email')}
                            onCodeChange={(val) => setVerificationCode('email', val)}
                            onVerify={() => verifyCode('email')}
                          />
                        </>
                        )}
                  </FormControl>
                )}
              </Stack>

              <Box>
                <Terms
                  value={termsAgreed}
                  onChange={(a) => {
                    setTermsAgreed(a)
                    logConsentEvent('processingCheckboxChanged', {
                      checked: a
                    })
                    updateSession({ processingConsentGiven: a })
                  }}
                />
              </Box>

              <Stack p={4} bg='bg.subtle' rounded='md' spacing={3}>
                <Heading size='md'>Trial coordinator's terms & privacy</Heading>
                <Text>
                  Please ensure you have read and understood the following
                  information before continuing.
                </Text>
                <Box bg='bg.surface' p={3} whiteSpace='pre-line'>
                  {trial?.conditions}
                </Box>
                <Checkbox
                  iconSize='lg'
                  colorPalette='green'
                  isChecked={conditionsConsentGiven}
                  onChange={(e) => {
                    setConditionsConsentGiven(e.checked)
                    logConsentEvent('trialConditionsCheckboxChanged', {
                      checked: e.checked
                    })
                    updateSession({ trialConditionsConsentGiven: e.checked })
                  }}
                >
                  I have read and agree to these terms
                </Checkbox>
              </Stack>

              <Stack p={4} bg='bg.subtle' rounded='md'>
                <Heading size='md'>Consent to take part in this trial</Heading>
                <Checkbox
                  id='consent'
                  isChecked={consentGiven}
                  onChange={(e) => {
                    setConsentGiven(e.checked)
                    logConsentEvent('consentCheckboxChanged', {
                      checked: e.checked
                    })
                    updateSession({ consentGiven: e.checked })
                  }}
                  alignItems='start'
                >
                  I have read and understood all of the information above, and I
                  consent to taking part in this trial. If I have provided
                  contact details, I consent to these being used to receive
                  trial-related communications and to verify my consent. I
                  understand that my electronic signature is the legally binding
                  equivalent of my handwritten signature and is linked to this
                  consent record and the consent document content that will be shown in my
                  signed consent.
                </Checkbox>
              </Stack>

              {error && (
                <Box
                  position='sticky'
                  bottom='5'
                  zIndex={8}
                  p={3}
                  shadow='md'
                  bg='bg.error'
                  borderWidth='1px'
                  borderColor='border.error'
                  rounded='md'
                >
                  <Text color='fg.error'>{error}</Text>
                </Box>
              )}

              {(!consentDocument.identityVerificationEnabled ||
                verificationComplete) && (
                  <Button
                    colorPalette='primary'
                    onClick={consent}
                    isLoading={loading}
                    isDisabled={!canSubmitConsent}
                  >
                    Submit consent
                  </Button>
              )}
            </>
          )}
        </>
      )}

      {hasFailedSaves && (
        <Box
          position='sticky'
          bottom='5'
          zIndex={9}
          p={3}
          shadow='md'
          bg='bg.warning'
          borderWidth='1px'
          borderColor='border.warning'
          rounded='md'
        >
          <Stack gap={2} w='100%'>
            <Text color='fg.warning'>
              Some answers have not saved yet. Retry failed saves before
              submitting consent.
            </Text>
            <Button
              size='sm'
              variant='outline'
              onClick={retryFailedSaves}
              isDisabled={hasPendingSaves}
            >
              Retry failed saves
            </Button>
          </Stack>
        </Box>
      )}

      {hasPendingSaves && (
        <Box
          position='sticky'
          bottom='5'
          zIndex={10}
          p={3}
          shadow='md'
          bg='bg.info'
          borderWidth='1px'
          borderColor='border.info'
          rounded='md'
        >
          <Stack direction='row' alignItems='center' gap={3} w='100%'>
            <Spinner size='sm' />
            <Text>Syncing your latest changes...</Text>
          </Stack>
        </Box>
      )}

      {!success && !trial?.eConsentClosed && (
        <Box textAlign='center'>
          <Button variant='ghost' size='xs' color='fg.subtle' onClick={resetConsent}>
            Reset and start a new consent session
          </Button>
        </Box>
      )}
    </Stack>
  )
}

function ScrollContinue ({ label = 'Scroll down to continue' }) {
  return (
    <Stack alignItems='center' gap={2} py={3}>
      <Box h='1px' w='full' bg='border.default' />
      <Stack alignItems='center' gap={1}>
        <Text fontSize='xs' color='fg.muted' fontWeight='medium' letterSpacing='wider' textTransform='uppercase'>{label}</Text>
        <Icon as={FaChevronDown} color='primary.500' _dark={{ color: 'primary.400' }} boxSize={5} />
      </Stack>
    </Stack>
  )
}
